<template>
  <div>
    <LoadingBar v-if="loading" message="Loading Work Order Details..." />

    <form v-else @submit.prevent="handleSubmit" id="workorder_edit_form" enctype="multipart/form-data">
      <div class="mb-3">
        <label class="form-label form-label-thin">Work Order Name <span class="text-danger">*</span></label>
        <input type="text" class="form-control form-control-thin" id="name" name="name"
          v-model="form.name" placeholder="Enter work order name" />
      </div>

      <div class="mb-3 form-group">
        <MultiSelect
          v-model="form.requests"
          :options="custom_requests"
          option-label="name"
          option-value="id"
          :multiple="true"
          label="Requests"
          placeholder="Select Requests"
        />
      </div>

      <div class="mb-3 form-group">
        <MultiSelect
          v-model="form.categories"
          :options="categories"
          option-label="id"
          option-value="id"
          :multiple="true"
          label="Categories"
          placeholder="Select Categories"
        />
        <div data-ie="categories"></div>
      </div>

      <div class="mb-4">
        <MultiSelect
          v-model="form.location"
          :options="locations"
          option-label="name"
          option-value="id"
          :multiple="false"
          label="Location"
          placeholder="Select Location"
        />
        <div data-ie="location"></div>
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Work Type <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.work_type" name="work_type">
          <option value="" disabled>Select Work Type</option>
          <option value="preventive">Preventive</option>
          <option value="corrective">Corrective</option>
          <option value="reactive">Reactive</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Priority <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.priority" name="priority">
          <option value="" disabled>Select Priority</option>
          <option value="low">🔵 Low</option>
          <option value="medium">🟡 Medium</option>
          <option value="high">🔴 High</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Assign To <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.assign_to" name="assign_to">
          <option value="" disabled>Select</option>
          <option value="team">Team</option>
          <option value="individual">Individual</option>
          <option value="vendor">Vendor/Contractor</option>
        </select>
      </div>

      <div class="mb-3" v-if="form.assign_to === 'team'">
        <MultiSelect
          v-model="form.team"
          :options="teams"
          option-label="name"
          option-value="id"
          :multiple="false"
          label="Team"
          placeholder="Select Team"
        />
        <div data-ie="team"></div>
      </div>

      <div class="mb-3" v-if="form.assign_to === 'individual'">
        <MultiSelect
          v-model="form.users"
          :options="custom_users"
          option-label="name"
          option-value="id"
          :multiple="true"
          label="Users/Inspectors"
          placeholder="Select Users"
        />
        <div data-ie="users"></div>
      </div>

      <div class="mb-3" v-if="form.assign_to === 'vendor'">
        <MultiSelect
          v-model="form.vendor"
          :options="custom_vendors"
          option-label="name"
          option-value="id"
          :multiple="false"
          label="Select Vendor/Contractor"
          placeholder="Select Vendor/Contractor"
          @update:model-value="onVendorChange"
        />
        <div data-ie="vendor"></div>
      </div>

      <div class="mb-3" v-if="form.assign_to === 'vendor' && form.vendor && vendorContacts.length > 0">
        <label class="form-label form-label-thin">Select Contact <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.vendor_contact" name="vendor_contact">
          <option value="" disabled>Select Contact</option>
          <option v-for="contact in vendorContacts" :key="contact.id" :value="contact.id">
            {{ contact.name || '' }} ({{ contact.email || '' }})
          </option>
        </select>
        <small class="text-muted">Select a contact person for this vendor</small>
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Assign Verifier <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.verifier" name="verifier">
          <option value="" selected disabled>Select Verifier</option>
          <option v-for="user in users" :key="user.id" :value="user.id">
            {{ user.first_name }} {{ user.last_name }}
          </option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Frequency <span class="text-danger">*</span></label>
        <select class="form-select form-select-thin" v-model="form.frequency" name="frequency">
          <option value="" disabled>Select Frequency</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      <div v-if="form.frequency && form.frequency !== 'custom'" class="mb-3">
        <label class="form-label form-label-thin">From Date <span class="text-danger">*</span></label>
        <input type="date" :min="new Date().toISOString().split('T')[0]" class="form-control form-control-thin" v-model="form.from_date" name="from_date" style="max-width: 200px; font-size: 12px;" />
      </div>

      <div v-if="form.frequency" class="mb-3 p-3 rounded" style="background: #f5f5f5; font-size: 13px;">
        <div v-if="form.frequency === 'daily'">
          <input type="hidden" name="freq" value="daily">
          <div class="fw-bold mb-2" style="font-size: 13px;">Everyday</div>
          <div class="d-flex flex-wrap form-group gap-2">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Sunday"> Sunday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Monday"> Monday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Tuesday"> Tuesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Wednesday"> Wednesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Thursday"> Thursday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Friday"> Friday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.exclude" value="Saturday"> Saturday
            </label>
          </div>
        </div>

        <div v-if="form.frequency === 'weekly'">
          <input type="hidden" name="freq" value="weekly">
          <div class="fw-bold mb-2" style="font-size: 13px;">Weekly</div>
          <div class="d-flex flex-wrap form-group gap-2">
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Monday"> Monday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Tuesday"> Tuesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Wednesday"> Wednesday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Thursday"> Thursday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Friday"> Friday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Saturday"> Saturday
            </label>
            <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
              <input type="checkbox" v-model="form.days" name="days" value="Sunday"> Sunday
            </label>
          </div>
        </div>

        <div v-if="form.frequency === 'monthly'">
          <input type="hidden" name="freq" value="monthly">
          <div class="fw-bold mb-2" style="font-size: 13px;">Monthly</div>
          <div class="mb-2">
            <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Week:</div>
            <div class="d-flex flex-wrap form-group gap-2 mt-1">
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 5" :key="i">
                <input type="checkbox" v-model="form.weeks" :value="i"> Week {{ i }}
              </label>
            </div>
          </div>
          <div>
            <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Days:</div>
            <div class="d-flex flex-wrap form-group gap-2 mt-1">
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Monday"> Monday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Tuesday"> Tuesday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Wednesday"> Wednesday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Thursday"> Thursday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Friday"> Friday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Saturday"> Saturday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Sunday"> Sunday
              </label>
            </div>
          </div>
        </div>

        <div v-if="form.frequency === 'yearly'">
          <input type="hidden" name="freq" value="yearly">
          <div class="fw-bold mb-2" style="font-size: 13px;">Yearly</div>
          <div class="mb-2">
            <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Month:</div>
            <div class="d-flex flex-wrap form-group gap-1 mt-1">
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 12" :key="i">
                <input type="checkbox" v-model="form.months" :value="i"> M{{ i }}
              </label>
            </div>
          </div>
          <div class="mb-2">
            <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Week:</div>
            <div class="d-flex flex-wrap form-group gap-2 mt-1">
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;" v-for="i in 5" :key="i">
                <input type="checkbox" v-model="form.weeks" :value="i"> Week {{ i }}
              </label>
            </div>
          </div>
          <div>
            <div class="fw-bold" style="font-size: 12px; color: #555;">Choose Days:</div>
            <div class="d-flex flex-wrap form-group gap-2 mt-1">
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Monday"> Monday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Tuesday"> Tuesday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Wednesday"> Wednesday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Thursday"> Thursday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Friday"> Friday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Saturday"> Saturday
              </label>
              <label class="d-flex align-items-center gap-1" style="font-size: 12px; cursor: pointer;">
                <input type="checkbox" v-model="form.days" value="Sunday"> Sunday
              </label>
            </div>
          </div>
        </div>

        <div v-if="form.frequency === 'custom'">
          <div class="fw-bold mb-2" style="font-size: 13px;">Schedule On</div>
          <div class="form-group">
            <input type="date" class="form-control form-control-thin" v-model="form.schedule_date" name="schedule_date" style="max-width: 200px; font-size: 12px;" />
          </div>
        </div>

        <div v-if="form.frequency">
          <div class="fw-bold mt-3 mb-2" style="font-size: 13px;">Times</div>
          <div id="time-container" class="row"></div>
          <div data-ie="times"></div>
          <button type="button" @click="addTimeInput()" class="btn btn-sm btn-success mt-2" style="font-size: 12px;">
            <i class="fas fa-clock"></i> Add Time
          </button>
        </div>
      </div>

      <div class="mb-3">
        <MultiSelect
          v-model="form.assets"
          :options="assets"
          option-label="name"
          option-value="id"
          :multiple="true"
          label="Asset"
          placeholder="Select Asset"
        />
      </div>

      <div class="mb-3">
        <label class="form-label form-label-thin">Description</label>
        <textarea class="form-control form-control-thin" id="description" name="description"
          v-model="form.description" placeholder="Enter description" rows="3"></textarea>
      </div>

      <div class="mb-4">
        <FileUploader v-model:files="form.files" maxFiles="5" 
          :help-text="'Upload related documents or images. Max 5 files.'" />
      </div>

      <div class="d-flex justify-content-end gap-2">
        <button type="button" class="btn btn-sm btn-outline-secondary hover-white" @click="close">
          <i class="fa fa-times"></i> Cancel
        </button>
        <button type="submit" class="btn btn-primary py-1 px-2 text-light hover-white btn-sm form-submit-btn" :disabled="submitting">
          <i class="fa fa-save"></i>
          {{ submitting ? 'Updating…' : 'Update' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'
import FileUploader from '@/components/FileUploader.vue'
import LoadingBar from '@/components/utils/LoadingBar.vue'

export default {
  name: 'EditWorkOrder',
  props: {
    workOrderId: {
      type: [String, Number],
      required: true
    }
  },
  components: {
    MultiSelect,
    FileUploader,
    LoadingBar
  },
  data() {
    return {
      loading: true,
      submitting: false,
      locations: [],
      teams: [],
      users: [],
      vendors: [],
      assets: [],
      categories: [],
      requests: [],
      custom_requests: [],
      custom_users: [],
      custom_vendors: [],
      vendorContacts: [],
      form: {
        name: '',
        location: '',
        work_type: '',
        priority: '',
        assign_to: '',
        team: '',
        users: [],
        vendor: '',
        vendor_contact: '',
        verifier: '',
        frequency: '',
        from_date: '',
        assets: [],
        categories: [],
        requests: [],
        description: '',
        exclude: [],
        days: [],
        weeks: [],
        months: [],
        times: [],
        schedule_date: '',
        files: null
      }
    }
  },
  mounted() {
    this.initializePage()
  },
  watch: {
    'form.frequency'(newVal) {
      if (newVal !== 'daily') this.form.exclude = []
      if (newVal !== 'weekly') this.form.days = []
      if (newVal !== 'monthly') {
        this.form.days = []
        this.form.weeks = []
      }
      if (newVal !== 'yearly') {
        this.form.days = []
        this.form.weeks = []
        this.form.months = []
      }
      const container = document.getElementById('time-container')
      if (container) {
        container.innerHTML = ''
      }
    }
  },
  methods: {
    async initializePage() {
      this.loading = true
      await this.getDropdownData()
      await this.loadWorkOrder()
      this.loading = false
    },

    onVendorChange() {
      this.form.vendor_contact = ''
      this.vendorContacts = []
      if (this.form.vendor) {
        const vendor = this.vendors.find(v => String(v.id) === String(this.form.vendor))
        if (vendor && vendor.get_contacts) {
          this.vendorContacts = vendor.get_contacts
        }
      }
    },

    addTimeInput(initialValue = '') {
      const container = document.getElementById('time-container')
      if (!container) return

      const row = document.createElement('div')
      row.className = 'row mb-1'

      const col = document.createElement('div')
      col.className = 'col-md-4'

      const inputGroup = document.createElement('div')
      inputGroup.className = 'input-group'

      const input = document.createElement('input')
      input.type = 'time'
      input.name = 'times[]'
      input.className = 'form-control form-control-thin'
      input.placeholder = 'Select time'
      input.style.fontSize = '12px'
      input.value = initialValue

      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className = 'btn btn-outline-danger btn-sm'
      removeBtn.innerHTML = 'X'
      removeBtn.style.fontSize = '12px'
      removeBtn.onclick = () => {
        const inputs = Array.from(container.querySelectorAll('input[type="time"]'))
        const index = inputs.indexOf(input)
        if (index > -1) {
          this.form.times.splice(index, 1)
        }
        row.remove()
      }

      inputGroup.appendChild(input)
      inputGroup.appendChild(removeBtn)
      col.appendChild(inputGroup)
      row.appendChild(col)
      container.appendChild(row)

      this.form.times.push(initialValue)

      input.addEventListener('change', () => {
        const inputs = Array.from(container.querySelectorAll('input[type="time"]'))
        const index = inputs.indexOf(input)
        if (index > -1) {
          this.form.times[index] = input.value
        }
      })
    },

    renderTimeInputs() {
      const container = document.getElementById('time-container')
      if (!container) return
      container.innerHTML = ''
      const existingTimes = Array.isArray(this.form.times) ? [...this.form.times] : []
      this.form.times = []
      existingTimes.forEach(time => this.addTimeInput(time))
    },

    async getDropdownData() {
      const result = await form_api_submit({
        url: 'wo/dropdowns',
        method: 'GET',
        show_success_toast: false
      })

      if (result.success) {
        this.requests = result.data.requests || []
        this.custom_requests = (this.requests || []).map(request => ({
          id: request.id,
          name: request.title || 'Unnamed Request'
        }))
        this.teams = result.data.teams || []
        this.users = result.data.users || []
        this.vendors = result.data.vendors || []
        this.assets = result.data.assets || []
        this.categories = result.data.categories || []

        this.custom_users = (this.users || []).map(user => ({
          id: user.id,
          name: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.external_id || 'Unknown'
        }))

        this.custom_vendors = (this.vendors || []).map(vendor => ({
          id: vendor.id,
          name: [vendor.first_name, vendor.last_name].filter(Boolean).join(' ') || vendor.external_id || 'Unknown'
        }))
      }
    },

    async loadWorkOrder() {
      if (!this.workOrderId) return
      const result = await form_api_submit({
        url: `wo/profile/${this.workOrderId}`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true
      })

      if (!result.success || !result.data) {
        return
      }

      const data = result.data
      this.form.name = data.name || ''
      this.form.location = data.get_location?.id || data.location?.id || data.location || ''
      this.form.work_type = data.work_type || ''
      this.form.priority = data.priority || ''
      this.form.assign_to = data.assign_to || ''
      this.form.team = data.get_team?.id || data.team || ''
      this.form.users = Array.isArray(data.get_users)
        ? data.get_users.map(user => user.id)
        : Array.isArray(data.users)
          ? data.users.map(user => user.id)
          : []
      this.form.vendor = data.get_vendor?.id || data.vendor || ''
      this.form.vendor_contact = data.vendor_contact || ''
      this.form.verifier = data.verifier?.id || data.verifier || data.get_verifier?.id || data.verifier_id || ''
      this.form.frequency = data.frequency || data.frequency_type || data.frequency_detail?.frequency || ''
      this.form.from_date = data.from_date || ''
      this.form.assets = Array.isArray(data.get_assets)
        ? data.get_assets.map(asset => asset.id)
        : Array.isArray(data.assets)
          ? data.assets.map(asset => asset.id)
          : []
      this.form.categories = Array.isArray(data.get_categories)
        ? data.get_categories.map(category => category.id)
        : Array.isArray(data.categories)
          ? data.categories.map(category => category.id)
          : []
      this.form.requests = Array.isArray(data.get_requests)
        ? data.get_requests.map(request => request.id)
        : Array.isArray(data.requests)
          ? data.requests.map(request => request.id)
          : []

      const normalizeToId = (value, list) => {
        if (!value || !Array.isArray(list)) return value
        const found = list.find(item => String(item.id) === String(value) || String(item.external_id) === String(value))
        return found ? found.id : value
      }

      this.form.location = normalizeToId(this.form.location, this.locations)
      this.form.team = normalizeToId(this.form.team, this.teams)
      this.form.vendor = normalizeToId(this.form.vendor, this.vendors)
      this.form.verifier = normalizeToId(this.form.verifier, this.users)
      this.form.description = data.description || ''
      this.form.exclude = data.frequency_detail?.exclude || []
      this.form.days = data.frequency_detail?.days || []
      this.form.weeks = data.frequency_detail?.weeks || []
      this.form.months = data.frequency_detail?.months || []
      this.form.schedule_date = data.frequency_detail?.schedule_date || data.schedule_date || ''
      this.form.times = data.frequency_detail?.times || []

      if (this.form.assign_to === 'vendor') {
        this.onVendorChange()
      }

      this.renderTimeInputs()
    },

    async handleSubmit() {
      this.submitting = true
      const times = this.form.times.filter(t => t !== '' && t !== null && t !== undefined)
      const formData = new FormData()

      formData.append('name', this.form.name || '')
      formData.append('location', this.form.location || '')
      formData.append('work_type', this.form.work_type || '')
      formData.append('priority', this.form.priority || '')
      formData.append('assign_to', this.form.assign_to || '')
      formData.append('team', this.form.team || '')
      formData.append('vendor', this.form.vendor || '')
      formData.append('vendor_contact', this.form.vendor_contact || '')
      formData.append('verifier', this.form.verifier || '')
      formData.append('frequency', this.form.frequency || '')
      formData.append('from_date', this.form.from_date || '')
      formData.append('description', this.form.description || '')
      formData.append('schedule_date', this.form.schedule_date || '')

      if (this.form.users && this.form.users.length > 0) {
        this.form.users.forEach(userId => {
          formData.append('users[]', userId)
        })
      }
      if (this.form.assets && this.form.assets.length > 0) {
        this.form.assets.forEach(assetId => {
          formData.append('assets[]', assetId)
        })
      }
      if (this.form.categories && this.form.categories.length > 0) {
        this.form.categories.forEach(categoryId => {
          formData.append('categories[]', categoryId)
        })
      }
      if (this.form.requests && this.form.requests.length > 0) {
        this.form.requests.forEach(requestId => {
          formData.append('requests[]', requestId)
        })
      }
      if (this.form.exclude && this.form.exclude.length > 0) {
        this.form.exclude.forEach(excludeDay => {
          formData.append('exclude[]', excludeDay)
        })
      }
      if (this.form.days && this.form.days.length > 0) {
        this.form.days.forEach(day => {
          formData.append('days[]', day)
        })
      }
      if (this.form.weeks && this.form.weeks.length > 0) {
        this.form.weeks.forEach(week => {
          formData.append('weeks[]', week)
        })
      }
      if (this.form.months && this.form.months.length > 0) {
        this.form.months.forEach(month => {
          formData.append('months[]', month)
        })
      }
      if (times && times.length > 0) {
        times.forEach(time => {
          formData.append('times[]', time)
        })
      }
      if (this.form.files && this.form.files.length > 0) {
        for (let i = 0; i < this.form.files.length; i++) {
          formData.append('files[]', this.form.files[i])
        }
      }

      const result = await form_api_submit({
        url: `wo/update/${this.workOrderId}`,
        method: 'POST',
        data: formData,
        take_form_data: false,
        curr_form_id: '#workorder_edit_form',
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (result.success) {
        this.$emit('updated', result.data)
        this.$emit('close')
        this.resetForm()
      }

      this.submitting = false
    },

    resetForm() {
      this.form = {
        name: '',
        location: '',
        work_type: '',
        priority: '',
        assign_to: '',
        team: '',
        users: [],
        vendor: '',
        vendor_contact: '',
        verifier: '',
        frequency: '',
        from_date: '',
        assets: [],
        categories: [],
        requests: [],
        description: '',
        exclude: [],
        days: [],
        weeks: [],
        months: [],
        times: [],
        schedule_date: '',
        files: null
      }
      this.vendorContacts = []
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.form-label-thin {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
}

.form-control-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-control-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-select-thin {
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1.5px solid #e0e0e0;
  transition: border-color 0.2s;
}

.form-select-thin:focus {
  border-color: #667eea;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}
</style>
